import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  makeStyles,
  Tooltip
} from "@material-ui/core";
import { HelpCircle } from "lucide-react";
import Markdown from "markdown-to-jsx";
import { manuals } from "./manuals";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    background: "linear-gradient(to right, #182229, #202d37)",
    color: "#ffffff",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
  },
  dialogTitleText: {
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "1.25rem",
  },
  dialogContent: {
    padding: "24px",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
    lineHeight: 1.6,
    "& h3": {
      marginTop: "20px",
      marginBottom: "10px",
      color: theme.palette.primary.main,
      fontSize: "1.15rem",
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
    },
    "& h4": {
      marginTop: "16px",
      marginBottom: "8px",
      fontSize: "1.05rem",
      fontWeight: 600,
    },
    "& p": {
      marginBottom: "12px",
      fontSize: "0.95rem",
    },
    "& ul": {
      paddingLeft: "20px",
      marginBottom: "16px",
      "& li": {
        marginBottom: "8px",
        fontSize: "0.95rem",
      }
    },
    "& hr": {
      border: 0,
      height: "1px",
      background: "rgba(0, 0, 0, 0.1)",
      margin: "24px 0",
    },
    "& code": {
      backgroundColor: "rgba(0, 0, 0, 0.05)",
      padding: "2px 6px",
      borderRadius: "4px",
      fontFamily: "monospace",
      fontSize: "0.9rem",
    },
    "& blockquote": {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      margin: "0 0 16px 0",
      padding: "8px 16px",
      backgroundColor: "rgba(0, 0, 0, 0.02)",
      fontStyle: "italic",
    }
  },
  dialogActions: {
    padding: "16px 24px",
    borderTop: "1px solid rgba(0, 0, 0, 0.1)",
    backgroundColor: theme.palette.background.default,
  },
  helpButton: {
    color: "#ffffff",
    borderRadius: "8px",
    padding: "8px",
    margin: "0 2px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      transform: "translateY(-1px)",
    },
  },
  constructionContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 20px",
    textAlign: "center",
  },
  constructionText: {
    marginTop: "16px",
    color: theme.palette.text.secondary,
  }
}));

const ManualButton = () => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Determinar a rota atual e resolver caminhos dinâmicos
  let currentPath = location.pathname;

  if (currentPath.startsWith("/tickets")) {
    currentPath = "/tickets";
  } else if (currentPath.startsWith("/flowbuilder")) {
    currentPath = "/flowbuilder";
  } else if (currentPath.endsWith("/report") && currentPath.startsWith("/campaign")) {
    currentPath = "/campaign-report";
  } else if (currentPath.startsWith("/campaigns-config")) {
    currentPath = "/campaigns-config";
  } else if (currentPath.startsWith("/campaign")) {
    currentPath = "/campaigns";
  } else if (currentPath.startsWith("/chats")) {
    currentPath = "/chats";
  } else if (currentPath.startsWith("/contact-lists")) {
    currentPath = "/contact-lists";
  } else if (currentPath.startsWith("/contacts")) {
    currentPath = "/contacts";
  } else if (currentPath === "/TagsKanban" || currentPath.toLowerCase().startsWith("/kanban")) {
    currentPath = "/Kanban";
  } else if (currentPath.startsWith("/schedules")) {
    currentPath = "/schedules";
  } else if (currentPath.startsWith("/birthday-settings")) {
    currentPath = "/birthday-settings";
  } else if (currentPath.startsWith("/tags")) {
    currentPath = "/tags";
  } else if (currentPath.startsWith("/todolist")) {
    currentPath = "/todolist";
  } else if (currentPath.startsWith("/connections")) {
    currentPath = "/connections";
  } else if (currentPath.startsWith("/files")) {
    currentPath = "/files";
  } else if (currentPath.startsWith("/settings")) {
    currentPath = "/settings";
  } else if (currentPath.startsWith("/queues")) {
    currentPath = "/queues";
  } else if (currentPath.startsWith("/users")) {
    currentPath = "/users";
  } else if (currentPath.startsWith("/messages-api")) {
    currentPath = "/messages-api";
  } else if (currentPath.startsWith("/financeiro")) {
    currentPath = "/financeiro";
  } else if (currentPath.startsWith("/prompts")) {
    currentPath = "/prompts";
  } else if (currentPath.startsWith("/queue-integration")) {
    currentPath = "/queue-integration";
  } else if (currentPath.startsWith("/announcements")) {
    currentPath = "/announcements";
  } else if (currentPath.startsWith("/backup")) {
    currentPath = "/backup";
  } else if (currentPath.startsWith("/reports")) {
    currentPath = "/reports";
  } else if (currentPath.startsWith("/moments")) {
    currentPath = "/moments";
  } else if (currentPath.startsWith("/whatsapp-consumption")) {
    currentPath = "/whatsapp-consumption";
  }

  const manual = manuals[currentPath];

  return (
    <>
      <Tooltip title="Manual da Tela" arrow>
        <IconButton
          onClick={handleOpen}
          aria-label="Abrir manual desta tela"
          className={classes.helpButton}
        >
          <HelpCircle size={22} />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        aria-labelledby="manual-dialog-title"
      >
        <DialogTitle id="manual-dialog-title" className={classes.dialogTitle} disableTypography>
          <Typography component="h2" className={classes.dialogTitleText}>
            📖 Manual: {manual ? manual.title : "Ajuda do Sistema"}
          </Typography>
        </DialogTitle>

        <DialogContent dividers className={classes.dialogContent}>
          {manual ? (
            <Markdown>{manual.content}</Markdown>
          ) : (
            <div className={classes.constructionContainer}>
              <HelpCircle size={64} style={{ color: "#ccc" }} />
              <Typography variant="h6" style={{ marginTop: 16, fontWeight: 600 }}>
                Manual em Construção
              </Typography>
              <Typography variant="body2" className={classes.constructionText}>
                O manual explicativo detalhado para esta tela (rota: <code>{location.pathname}</code>) está sendo elaborado e estará disponível em breve.
              </Typography>
            </div>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={handleClose}
            color="primary"
            variant="contained"
            style={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
          >
            Entendi, fechar manual
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManualButton;
